import React, { Component } from "react";
import { colors } from "./theme";

// "Your HR practices in numbers - Benchmark Analysis".
//
// Each row compares one metric against the Employer Partner average. The rows
// have different units (days, EUR, hours, %), so there is no shared axis:
// instead every bar is drawn at 50% of the track when the organization is
// exactly on the average, which puts the average on the same vertical guide for
// every row regardless of unit. This is the same scale V1 uses.
//
// `rows` is [{ label, value, average, unit }], where `label` is the sentence
// from the report query with an XXX placeholder for the organization's value.
export default class V2BenchmarkBars extends Component {
  // The report query cannot store a null in a numeric column, so two sentinel
  // values stand in for the missing cases. Same values as V1.
  //
  // Rows that come from a JSON answer (the diversity percentages) bypass that
  // query and arrive as a plain null or "" when unanswered, so those are
  // treated as missing too - otherwise XXX is replaced with nothing and the
  // sentence renders as a bare unit ("... u organizaciji: %").
  _formatValue = (value) => {
    if (value === 0.5111) return "n/a";
    if (value === 0.5112) return "0";
    if (value === null || value === undefined || value === "") return "n/a";
    return value;
  };

  // The sentence arrives with an XXX placeholder where the organization's own
  // value goes, e.g. "average time to fill: <strong>XXX</strong> days".
  //
  // When the value is missing the unit is dropped with it, so the row reads
  // "...: n/a" rather than "...: n/a EUR". The unit is matched explicitly from
  // row.unit rather than by pattern, because in some labels XXX comes first and
  // the words after it are the sentence, not a unit
  // ("XXX sati uloženo u usavršavanje po zaposleniku").
  _label = (row) => {
    const value = this._formatValue(row.value);
    const label = String(row.label || "").replace("XXX", value);

    if (value !== "n/a") {
      return label;
    }

    // Only a unit that ENDS the sentence is dropped. Where XXX comes first the
    // following word is part of the sentence and has to stay, even though it
    // matches row.unit ("XXX sati uloženo u usavršavanje po zaposleniku").
    const trailing = ["%", ` ${row.unit}`];

    for (const suffix of trailing) {
      if (row.unit && label.endsWith(`n/a</strong>${suffix}`)) {
        return label.slice(0, -suffix.length);
      }
    }

    return label;
  };

  render() {
    const { rows, averageLabel } = this.props;

    if (!rows || !rows.length) {
      return null;
    }

    return (
      <div className="report-v2__benchmark">
        <p className="report-v2__benchmark-legend">{averageLabel}</p>

        <div className="report-v2__benchmark-rows">
          {rows.map((row, key) => {
            const value = parseFloat(row.value) || 0;
            const average = parseFloat(row.average) || 0;
            const hasAverage =
              row.average !== null &&
              row.average !== undefined &&
              row.average !== "";
            // On the average = half the track, so the guide lines up across
            // rows. Clamped to 0-100: a value far above the average would run
            // past the track, and a negative one (a loss-making year) would
            // give a negative width that the browser renders full-width.
            const valueWidth = average
              ? Math.max(0, Math.min(100, (50 * value) / average))
              : 0;

            return (
              <div className="report-v2__benchmark-row" key={key}>
                <span
                  className="report-v2__benchmark-label"
                  dangerouslySetInnerHTML={{ __html: this._label(row) }}
                />
                <div className="report-v2__benchmark-track">
                  <div
                    className="report-v2__benchmark-bar"
                    style={{
                      width: `${valueWidth}%`,
                      background: colors.secondary,
                    }}
                  />
                  {/* No average when no other organisation answered this. */}
                  {hasAverage && (
                    <span className="report-v2__benchmark-average">
                      {row.average}
                      {row.unit ? ` ${row.unit}` : ""}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}
